import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, ShieldCheck, Bot } from "lucide-react";

export function ChatDock({ assignmentId }) {
  const { refreshBalance } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) api.get(`/assignments/${assignmentId}/chat`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [open, assignmentId]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: q }, { id: "stream", role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assignments/${assignmentId}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, id: `a-${Date.now()}`, content: err.detail || "Something went wrong." } : x));
        setStreaming(false); refreshBalance(); return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, content: acc } : x));
      }
      setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, id: `a-${Date.now()}` } : x));
      refreshBalance();
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === "stream" ? { ...x, id: `a-${Date.now()}`, content: "Connection error. Please try again." } : x));
    } finally { setStreaming(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="open-chat-btn" className="fixed bottom-20 right-5 z-50 h-14 w-14 rounded-full bg-[#0B5D3B] p-0 text-white shadow-lg hover:bg-[#08472D] md:bottom-6">
          <MessageCircle className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-md" data-testid="chat-dock">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle className="flex items-center gap-2"><Bot className="h-5 w-5 text-[#0B5D3B] dark:text-[#25D366]" />Assignment AI tutor</SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex items-start gap-2 rounded-xl bg-accent/60 p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0B5D3B] dark:text-[#25D366]" />
            Each message uses 2 tokens. I explain and help you plan — I won't write your assignment.
          </div>
          {messages.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">Ask about your brief, rubric, or how to strengthen a section.</p>
          )}
          {messages.map((m) => (
            <div key={m.id} data-testid={`chat-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-[#0B5D3B] text-white" : "bg-accent text-foreground"}`}>
                {m.content || (streaming ? "…" : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-3">
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…" data-testid="chat-input" disabled={streaming} />
            <Button onClick={send} disabled={streaming} data-testid="chat-send-btn" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
